import { useState, useEffect } from "react";
import { Form, Input, Button, Upload as AntUpload, Card, message } from "antd";
import { Upload, CheckCircle, FileText, Loader2 } from "lucide-react";
import axios from '../../../services/axios';
import { useLocation, useNavigate } from 'react-router-dom';

const UploadDocumentForm = () => {
  const location = useLocation();
  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isReupload, setIsReupload] = useState(false);
  const [originalDocumentCode, setOriginalDocumentCode] = useState('');
  const [documentCategories, setDocumentCategories] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);  
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [form] = Form.useForm();
  const projectName = JSON.parse(localStorage.getItem('Project'));
  const user = localStorage.getItem('user');

  useEffect(() => {
      getUserDetails();
  
      if (location.state?.prefillData) {
        const prefillData = location.state.prefillData;
        setIsReupload(true);
        setOriginalDocumentCode(prefillData.originalDocumentCode);
        form.setFieldsValue({
          documentReference: prefillData.documentReference,
          paymentApplicationReference: prefillData.paymentApplicationReference,
          preliminary: prefillData.preliminary,
          workDone: prefillData.workDone,
          provisionalSum: prefillData.provisionalSum,
          contingency: prefillData.contingency,
          addAdvance: prefillData.addAdvance,
          lessAdvance: prefillData.lessAdvance,
          addMaterialOnSite: prefillData.addMaterialOnSite,
          lessRetention: prefillData.lessRetention,
          vat: prefillData.vat,
          grossPayableAmount: prefillData.grossPayableAmount,
          lessOtherDeductibles: prefillData.lessOtherDeductibles,
        });
      }
    }, [location.state, form]);

    const getUserDetails = async () => {
      try {
        const res = await axios.post('get-user-details', { user });
        if (res?.data?.userDetails) {
          if (res?.data?.userDetails.documentType) {
            setDocumentTypes(res.data.userDetails.documentType)
            if (res.data.userDetails.documentType.length === 1) {
              form.setFieldsValue({
                type: res.data.userDetails.documentType[0]
              });
            }
          }
          if (res?.data?.userDetails.documentCategory) {
            setDocumentCategories(res.data.userDetails.documentCategory);
            if (res.data.userDetails.documentCategory.length === 1) {
              form.setFieldsValue({
                category: res.data.userDetails.documentCategory[0]
              });
            }
          }
        }
      } catch (err) {
         if (err.response && err.response.data && err.response.data.msg) {
                message.warning(err.response.data.msg);
              } else if (err.response && err.response.data && err.response.data.userName) {
                message.warning(err.response.data.userName[0]);
              } else if (err.response && err.response.status === 401) {
                message.error('User not authorized');
              } else {
                message.error('Something went wrong 12');
              }
      }
  
    };

    const handleUpload = async (values) => {
      values.user = user;
      values.projectName = projectName;
  
      if (isReupload && originalDocumentCode) {
        values.code = originalDocumentCode;
      }
      
      if (fileList.length === 0) {
        message.warning("Please select a file.");
        return;
      }
    
      const selectedFile = fileList[0];
    
      const formData = new FormData();
      formData.append("file", selectedFile);
      const data = { ...values };
      formData.append("data", JSON.stringify(data));
      
      setLoading(true);
      try {
        if (isReupload) {
          const response = await axios.post("/pa-reupload-document", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            timeout: 600000
          } );
          if (response.status === 201) {
            message.success("File uploaded successfully!");
            form.resetFields();
            setFileList([]);
            setUploadSuccess(true);
            setTimeout(() => setUploadSuccess(false), 3000);
          } 
        } else {
  
        const response = await axios.post("/upload-payment-application", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 600000,
        });
    
        if (response.status === 201) {
          message.success("File uploaded successfully!");
          form.resetFields();
          setFileList([]);
          setUploadSuccess(true);
          setTimeout(() => setUploadSuccess(false), 3000);
        } else {
          throw new Error("Upload failed");
        }
      }
    
      } catch (err) {
         if (err.response && err.response.data && err.response.data.msg) {
                message.warning(err.response.data.msg);
              } else if (err.response && err.response.data && err.response.data.userName) {
                message.warning(err.response.data.userName[0]);
              } else if (err.response && err.response.status === 401) {
                message.error('User not authorized');
              } else {
                message.error('Something went wrong');
              }
      }
      setLoading(false);
    };
  

  return (
    <div className="relative flex-1 p-5 overflow-y-auto ml-2 mr-2 min-h-screen">
      <div className="max-w-3xl mx-auto">
        {/* <h2 className="text-2xl font-semibold text-gray-800 flex items-center justify-center space-x-2 p-2">
          <FileText className="text-blue-500 w-6 h-6" />
          <span>{isReupload ? "Reupload Document" : "Upload Document"}</span>
        </h2> */}

        <Card className="shadow-md rounded-lg p-6">
          <Form form={form} layout="vertical" 
          onFinish={handleUpload}>
            <Form.Item label="Document Reference" name="documentReference" rules={[{ required: true, message: "Please enter document reference" }]}>
              <Input placeholder="Enter document reference" />
            </Form.Item>

            <Form.Item label="Payment Application Reference" name="paymentApplicationReference" rules={[{ required: true, message: "Please enter payment application reference" }]}>
              <Input placeholder="Enter payment application reference" />
            </Form.Item>

            <Form.Item label="Preliminary" name="preliminary" rules={[{ required: true, message: "Please enter preliminary amount" }]}>
              <Input placeholder="Enter preliminary amount" type="number" />
            </Form.Item>

            <Form.Item label="Work Done" name="workDone" rules={[{ required: true, message: "Please enter work done amount" }]}>
              <Input placeholder="Enter work done amount" type="number" />
            </Form.Item>

            <Form.Item label="Provisional Sum" name="provisionalSum" rules={[{ required: true, message: "Please enter provisional sum" }]}>
              <Input placeholder="Enter provisional sum" type="number" />
            </Form.Item>

            <Form.Item label="Contingency" name="contingency" rules={[{ required: true, message: "Please enter contingency amount" }]}>
              <Input placeholder="Enter contingency amount" type="number" />
            </Form.Item>

            <Form.Item label="Add: Advance" name="addAdvance" rules={[{ required: true, message: "Please enter added advance" }]}>
              <Input placeholder="Enter added advance" type="number" />
            </Form.Item>

            <Form.Item label="Less: Advance" name="lessAdvance" rules={[{ required: true, message: "Please enter deducted advance" }]}>
              <Input placeholder="Enter deducted advance" type="number" />
            </Form.Item>

            <Form.Item label="Add: Material On Site" name="addMaterialOnSite" rules={[{ required: true, message: "Please enter material on site amount" }]}>
              <Input placeholder="Enter material on site amount" type="number" />
            </Form.Item>

            <Form.Item label="Less: Retention" name="lessRetention" rules={[{ required: true, message: "Please enter retention amount" }]}>
              <Input placeholder="Enter retention amount" type="number" />
            </Form.Item>

            <Form.Item label="Less: Other Deductibles" name="lessOtherDeductibles" rules={[{ required: true, message: "Please enter other deductibles amount" }]}>
              <Input placeholder="Enter other deductibles amount" type="number" />
            </Form.Item>

            <Form.Item label="VAT @ 5%" name="vat" rules={[{ required: true, message: "Please enter VAT amount" }]}>
              <Input placeholder="Enter VAT amount" type="number" />
            </Form.Item>

            <Form.Item label="Gross Payable Amount" name="grossPayableAmount" rules={[{ required: true, message: "Please enter gross payable amount" }]}>
              <Input placeholder="Enter gross payable amount" type="number" />
            </Form.Item>

            <Form.Item label="Payment Due Date" name="paymentDueDate" rules={[{ required: true, message: "Please select a payment due date" }]}>
              <Input type="date" />
            </Form.Item>

            <Form.Item label="Upload File" name="uploadFile" rules={[{ required: true, message: "Please upload a file" }]}>
              <AntUpload
                fileList={fileList}
                beforeUpload={(file) => {
                  setFileList([file]);
                  return false;
                }}
                onRemove={() => setFileList([])}
              >
                <Button icon={<Upload className="w-4 h-4" />}>Upload File</Button>
              </AntUpload>
            </Form.Item>
            <Form.Item className="mb-0">
              <Button 
                type="primary"
                htmlType="submit"
                className="w-full h-10 flex items-center justify-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{isReupload ? "Reuploading..." : "Uploading..."}</span>
                  </>
                ) : (
                  isReupload ? "Reupload Document" : "Upload Document"
                )}
              </Button>
            </Form.Item>

            {uploadSuccess && (
              <div className="flex items-center gap-2 text-green-500 mt-4">
                <CheckCircle className="w-5 h-5" />
                <span>File uploaded successfully!</span>
              </div>
            )}
          </Form>
        </Card>
      </div>
    </div>
  );
};

export default UploadDocumentForm;
